// AtriumSuperAdminHome.tsx — Atrium Super Admin home, "sa-home" (Sprint 84, ADR-082).
// ----------------------------------------------------------------------------
// The Salesforce-style platform-overview home. The prototype's "multi-org"
// surface maps to FIRM-TIER in production, but the only tenancy-safe portfolio a
// super-admin can see today is `authStore.availableCondos` — the condos the
// backend already authorised for this user (scoped per role by /condos/available,
// see authStore.fetchAvailableCondos). We present THOSE as the portfolio. We do
// NOT invent a multi-org API that bypasses tenancy / FirmGuard.
//
// HONESTY CONTRACT (ADR-082, extended Sprint 115 / ADR-108):
//   • Organizations count / Units managed → REAL (availableCondos + totalUnits).
//   • Active owners (the per-condo active-user roll-up; relabeled Sprint 118 /
//     R8-LOW-4 from "Active users" so the label matches the count's scope),
//     outstanding dues, collected YTD, collection rate (portfolio
//     + per-org), open work orders, active violations → REAL, from
//     GET /api/portfolio/overview (Sprint 115). The endpoint rolls up the SAME
//     tenancy-scoped condo set (super-admin → all authorised condos) the rest of
//     this home already uses, so nothing cross-tenant is exposed.
//   • Per-org units/status → REAL where the Condo record carries the field
//     (totalUnits, isActive). The per-org "uptime" column was platform-ops with
//     NO source — Sprint 115 replaced it with the REAL per-org collection rate.
//   • MRR / API uptime / Open incidents → STILL no data source (no billing-of-
//     firms, no status feed): rendered as "—" and labelled "not available",
//     NEVER a fabricated number.
//   • Performance "platform growth" chart → we have no historical units-managed
//     series; the card is shown as a labelled PREVIEW (flat current total), not
//     a fabricated growth curve.
import React, { useEffect, useMemo, useState } from 'react';
import { Result } from 'antd';
import {
  PlusOutlined,
  FileTextOutlined,
  BankOutlined,
  TeamOutlined,
  DollarOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';

import { useAuthStore } from '../../../store/authStore';
import { apiService } from '../../../services/api';
import type { PortfolioOverview } from '../../../types';
import { useAtriumNav } from '../atriumNav';
import { currency } from '../../../utils/currency';

import { SFGreeting, type SFKpiChip } from './SFGreeting';
import { AtriumAreaChart } from './AreaChart';
import { Assistant, type AssistantItem } from './Assistant';
import dayjs from 'dayjs';
import './homeStyles.css';

const PREVIEW = '—';

export const AtriumSuperAdminHome: React.FC = () => {
  const { user, availableCondos, currentCondo, fetchAvailableCondos } = useAuthStore();

  // Sprint — F6. Shell navigator (no-op outside the Atrium shell). Same
  // mechanism the Owner home uses for its CTAs (atriumNav `go(key)`).
  const { go } = useAtriumNav();

  // Reuse the EXISTING tenancy-scoped action — /condos/available is scoped per
  // role by the backend (super-admin → all authorised condos). No new endpoint.
  useEffect(() => {
    fetchAvailableCondos();
  }, [fetchAvailableCondos]);

  // Sprint 115 — REAL portfolio aggregates. GET /api/portfolio/overview rolls up
  // the SAME tenancy-scoped condo set (resolved server-side, super-admin → all)
  // and returns active users, outstanding fees, collected YTD, collection rate,
  // open work orders, active violations — all from real data. Best-effort: if it
  // fails (older backend / no access) we fall back to the availableCondos-derived
  // capacity figures and the financial tiles stay labelled "not available".
  const [overview, setOverview] = useState<PortfolioOverview | null>(null);
  useEffect(() => {
    let cancelled = false;
    apiService.getPortfolioOverview()
      .then(o => { if (!cancelled) setOverview(o); })
      .catch(() => { if (!cancelled) setOverview(null); });
    return () => { cancelled = true; };
  }, []);

  // Format a number as CAD currency (no cents — these are portfolio-level sums).
  // Delegates to the one shared helper (Sprint 138 / ADR-133); this home was
  // already correctly CAD and is the reference the others were reconciled to.
  const money = (n: number) => currency(n, { cents: false });

  // Per-condo overview rows keyed by id, so the Organizations table can show a
  // REAL per-org collection rate instead of a fabricated "uptime" placeholder.
  const rowByCondoId = useMemo(() => {
    const m: Record<string, PortfolioOverview['condos'][number]> = {};
    (overview?.condos ?? []).forEach(r => { m[r.condoId] = r; });
    return m;
  }, [overview]);

  // Role guard — only super-admins (legacy aliases included) reach this home.
  const role = user?.role as string | undefined;
  const isSuperAdmin = role === 'super_admin_condo' || role === 'superadmin' || role === 'super_admin';

  // REAL — the portfolio is the set of condos the backend authorised for this
  // super-admin. Tenancy-safe: this is availableCondos, not a bypass query.
  const orgs = availableCondos;
  // NOTE: `c.totalUnits` is the condo's CONFIGURED CAPACITY (CondoCorp.total_units),
  // not a count of actual unit rows. So this rollup is "units of configured
  // capacity across the portfolio", NOT actual occupancy. Labels below say
  // "capacity" so we don't imply an actual-unit count. FOLLOW-UP: a true
  // actual-unit-row rollup needs a backend portfolio endpoint returning a real
  // per-condo unit-row count (out of scope here; availableCondos doesn't carry it).
  const totalUnits = useMemo(() => orgs.reduce((s, c) => s + (c.totalUnits ?? 0), 0), [orgs]);
  const activeOrgs = orgs.filter(c => c.isActive).length;

  // Sprint — F6. Client-side CSV export of the SAME portfolio rows rendered in
  // the Organizations table. No backend call: we serialise the `orgs` array
  // already in state and trigger a Blob download. Values are RFC-4180 quoted so
  // commas/quotes in org names don't break columns.
  const exportOrgsCsv = () => {
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Organization', 'Units (capacity)', 'City', 'State/Province', 'Status'];
    const rows = orgs.map(c => [
      esc(c.name),
      esc(c.totalUnits ?? 0),
      esc(c.city ?? ''),
      esc(c.state ?? ''),
      esc(c.isActive ? 'Active' : 'Inactive'),
    ].join(','));
    const csv = [header.map(esc).join(','), ...rows].join('\r\n');
    // Prepend a UTF-8 BOM so Excel renders accented org names correctly.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Sprint 145 (R14-9) — same UTC-stamp class as the `.split('T')[0]` sites,
    // spelled with `.slice(0, 10)`. An export taken at 21:00 EDT was named for
    // the next day.
    a.download = `organizations-${dayjs().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Guard AFTER all hooks (hooks must run unconditionally on every render).
  if (!isSuperAdmin) {
    return (
      <Result
        status="403"
        title="Platform overview"
        subTitle="This view is only available to platform administrators."
        style={{ paddingTop: 60 }}
      />
    );
  }

  // ── KPI chips — REAL counts where backed; platform-ops metrics marked preview ──
  // Active users + collection rate come from the portfolio overview (REAL). MRR
  // has no billing-of-firms data source, so it stays a labelled "preview".
  const chips: SFKpiChip[] = [
    { l: 'Organizations', v: String(orgs.length) },
    { l: 'Units (capacity)', v: totalUnits.toLocaleString() },
    // Sprint 138 (R12-14): the "Active owners" label was relabeled in Sprint 118
    // (R8-LOW-4) but the number stayed an active-USER count — it still included
    // admins/managers (32 vs 31 owners), so the earlier "label matches the number"
    // claim was false. The backend roll-up is now role-filtered to OWNER
    // (PortfolioOverview.activeOwners), so label and value finally agree.
    overview
      ? { l: 'Active owners', v: overview.activeOwners.toLocaleString() }
      : { l: 'Active owners', v: PREVIEW, d: 'preview' },
    overview
      ? { l: 'Collection rate', v: `${overview.collectionRatePct}%` }
      : { l: 'Monthly revenue', v: PREVIEW, d: 'preview' },
  ];

  // Assistant — only REAL, derivable signals. No fabricated license/billing rows.
  const assistantItems: AssistantItem[] = [];
  const inactive = orgs.filter(c => !c.isActive);
  if (inactive.length > 0) {
    assistantItems.push({
      tone: 'amber',
      h: `${inactive.length} inactive organization(s)`,
      s: 'Marked inactive in your portfolio',
      action: 'Review',
      // F6 — review inactive orgs in the Condominiums management list.
      onAction: () => go('condos'),
    });
  }
  assistantItems.push({
    tone: 'blue',
    h: `${orgs.length} organization(s) in your portfolio`,
    s: currentCondo ? `Currently viewing ${currentCondo.name}` : 'No active organization selected',
    action: 'Manage',
    // F6 — "Manage" the portfolio → the Condominiums management surface (the
    // org list where condos are added/edited/activated). This is the closest
    // real destination to the item's intent.
    onAction: () => go('condos'),
  });

  // Performance — we have no historical units-managed series, so this is a
  // labelled PREVIEW: a flat line at the current total, NOT a fabricated curve.
  const flatSeries = [totalUnits, totalUnits, totalUnits, totalUnits, totalUnits, totalUnits];
  const flatLabels = ['', '', '', '', '', 'now'];

  return (
    <div className="wpage wide fadein">
      <SFGreeting
        hi="Platform overview"
        badge="SUPER ADMIN"
        sub={<span>{orgs.length} organization{orgs.length === 1 ? '' : 's'} · {totalUnits.toLocaleString()} units of configured capacity</span>}
        chips={chips}
        btns={
          <>
            <button
              className="hb ghost"
              type="button"
              onClick={exportOrgsCsv}
              disabled={orgs.length === 0}
              title={orgs.length === 0 ? 'No organizations to export' : 'Export organizations to CSV'}
            >
              <FileTextOutlined /> Export
            </button>
            <button
              className="hb solid"
              type="button"
              onClick={() => go('condos')}
              title="Add a new organization (Condominiums)"
            >
              <PlusOutlined /> New org
            </button>
          </>
        }
      />

      <div className="sfgrid">
        <div className="sfcol">
          {/* Performance — PREVIEW (no historical series available) */}
          <div className="c p perf">
            <div className="pf-top">
              <div>
                <div className="ch" style={{ marginBottom: 0 }}><h3>Platform growth · units (configured capacity)</h3></div>
                <div className="pf-big num">{totalUnits.toLocaleString()}</div>
                <div className="pf-goal">
                  <span className="home-note">
                    Preview · total configured capacity across your portfolio (not actual unit-row counts). Historical growth series not yet available.
                  </span>
                </div>
              </div>
            </div>
            <AtriumAreaChart
              points={flatSeries}
              labels={flatLabels}
              color="#6D4AE0"
              ariaLabel={`Platform growth preview: ${totalUnits.toLocaleString()} units currently managed across your portfolio. Historical growth series is not yet available, so the line is flat at the current total.`}
            />
          </div>

          {/* Organizations table — REAL portfolio (availableCondos) */}
          <div className="c p">
            <div className="ch"><h3>Organizations</h3><span className="lk" style={{ cursor: 'default' }}>{orgs.length} total</span></div>
            {orgs.length === 0 && (
              <div className="rempty" style={{ padding: '12px 0' }}>No organizations available to your account.</div>
            )}
            {orgs.length > 0 && (
              <table className="tb" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--mut)', fontSize: 11.5, textTransform: 'uppercase' }}>
                    <th style={{ padding: '8px 6px' }}>Organization</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right' }}>Units (capacity)</th>
                    <th style={{ padding: '8px 6px' }}>Location</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right' }}>Collection rate</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map(c => {
                    const initials = c.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
                    return (
                      <tr key={c.id} style={{ borderTop: '1px solid var(--line)' }}>
                        <td style={{ padding: '11px 6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{
                              width: 28, height: 28, borderRadius: 8, background: 'var(--blue-s)',
                              color: 'var(--blue)', fontWeight: 800, fontSize: 11,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
                            }}>{initials}</span>
                            <span style={{ fontWeight: 700 }}>{c.name}</span>
                          </div>
                        </td>
                        <td style={{ padding: '11px 6px', textAlign: 'right', fontWeight: 700 }}>
                          {(c.totalUnits ?? 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '11px 6px', color: 'var(--mut)' }}>
                          {[c.city, c.state].filter(Boolean).join(', ') || PREVIEW}
                        </td>
                        {/* Per-org collection rate — REAL (from the portfolio overview,
                            keyed by condoId). "—" only until the overview loads. */}
                        <td style={{ padding: '11px 6px', textAlign: 'right', color: 'var(--mut)' }}>
                          {rowByCondoId[c.id] ? `${rowByCondoId[c.id].collectionRatePct}%` : PREVIEW}
                        </td>
                        <td style={{ padding: '11px 6px', textAlign: 'right' }}>
                          <span className={'bd ' + (c.isActive ? 'green' : 'gray')}>
                            <span className="pd" />{c.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Portfolio financials — REAL (from /api/portfolio/overview). Active
              users, outstanding fees, collected YTD, collection rate are all
              computed from data; rendered "—" only until the overview loads. */}
          <div className="statrow" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
            <div className="stat">
              <div className="gi"><TeamOutlined /></div>
              <div className="v">{overview ? overview.activeOwners.toLocaleString() : PREVIEW}</div>
              <div className="l">Active owners</div>
              <div className="d up">across portfolio</div>
            </div>
            <div className="stat">
              <div className="gi"><DollarOutlined /></div>
              <div className="v num">{overview ? money(overview.outstandingFees) : PREVIEW}</div>
              <div className="l">Outstanding dues</div>
              <div className="d nu">portfolio total</div>
            </div>
            <div className="stat">
              <div className="gi"><BankOutlined /></div>
              <div className="v num">{overview ? money(overview.collectedYtd) : PREVIEW}</div>
              <div className="l">Collected YTD</div>
              <div className="d up">portfolio total</div>
            </div>
            <div className="stat">
              <div className="gi"><ApartmentOutlined /></div>
              <div className="v">{overview ? `${overview.collectionRatePct}%` : PREVIEW}</div>
              <div className="l">Collection rate</div>
              <div className="d up">paid / billed</div>
            </div>
          </div>

          {/* Operations stat row — REAL (open work orders + active violations).
              MRR / API uptime / open incidents have NO data source today, so
              they are labelled "not available" rather than shown as fake numbers. */}
          <div className="statrow" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
            <div className="stat">
              <div className="gi"><FileTextOutlined /></div>
              <div className="v">{overview ? overview.openWorkOrders.toLocaleString() : PREVIEW}</div>
              <div className="l">Open work orders</div>
              <div className="d nu">across portfolio</div>
            </div>
            <div className="stat">
              <div className="gi"><BankOutlined /></div>
              <div className="v">{overview ? overview.activeViolations.toLocaleString() : PREVIEW}</div>
              <div className="l">Active violations</div>
              <div className="d nu">across portfolio</div>
            </div>
            <div className="stat">
              <div className="gi"><DollarOutlined /></div>
              <div className="v">{PREVIEW}</div>
              <div className="l">MRR</div>
              <div className="d nu">not available</div>
            </div>
            <div className="stat">
              <div className="gi"><ApartmentOutlined /></div>
              <div className="v">{PREVIEW}</div>
              <div className="l">API uptime · 30d</div>
              <div className="d nu">not available</div>
            </div>
          </div>
        </div>

        {/* Right rail */}
        <div className="sfcol">
          <Assistant items={assistantItems} />

          {/* Portfolio quick stats — REAL */}
          <div className="statrow" style={{ gridTemplateColumns: 'repeat(2,1fr)' }}>
            <div className="stat">
              <div className="gi"><BankOutlined /></div>
              <div className="v">{orgs.length}</div>
              <div className="l">Organizations</div>
              <div className="d up">{activeOrgs} active</div>
            </div>
            <div className="stat">
              <div className="gi"><ApartmentOutlined /></div>
              <div className="v num">{totalUnits.toLocaleString()}</div>
              <div className="l">Units (capacity)</div>
              <div className="d nu">configured capacity</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AtriumSuperAdminHome;
